import { gql, useQuery, useMutation } from "@apollo/client";
import {
  Box,
  Button,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { useNavigate } from "react-router-dom";
import DeleteConfirmationDialog, {
  useDeleteConfirmation,
} from "../Components/DeleteConfirmationDialog";

export default function MyEventsPage() {
  return (
    <div>
      <Typography variant="h4" gutterBottom>
        My Events
      </Typography>

      <EventsTable />
    </div>
  );
}

function EventsTable() {
  const { data, loading, error, refetch } = useQuery(MY_EVENTS);
  const navigate = useNavigate();
  const { deleteState, openDeleteDialog, closeDeleteDialog, setLoading } =
    useDeleteConfirmation();
  const [doDelete] = useMutation(
    gql`
      mutation ($eventId: String!) {
        deleteEvent(eventId: $eventId)
      }
    `
  );

  const handleDeleteEvent = (event) => {
    openDeleteDialog(event, "event");
  };

  const handleDeleteEventConfirmed = async (deleteReason) => {
    setLoading(true);
    try {
      await doDelete({
        variables: {
          eventId: deleteState.item.id,
          deleteReason,
        },
      });
      await refetch();
      closeDeleteDialog();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete event: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    refetch();
  }, []);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <Box py={2}>
      <DeleteConfirmationDialog
        open={deleteState.open}
        onClose={closeDeleteDialog}
        onConfirm={handleDeleteEventConfirmed}
        title="Delete Event"
        itemName={deleteState.item?.name}
        itemType="event"
        loading={deleteState.loading}
      />
      <TableContainer sx={{ pb: 2 }}>
        <Table>
          <TableHead>
            <TableCell>#</TableCell>
            <TableCell>ID</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Roles</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableHead>
          <TableBody>
            {data?.events?.map((event, i) => (
              <TableRow key={event.id}>
                <TableCell>{i + 1}</TableCell>
                <TableCell>{event.id}</TableCell>
                <TableCell>{event.name}</TableCell>
                <TableCell>{event.roles}</TableCell>
                <TableCell>
                  {format(+event.created, "d MMM, yyyy h:m a")}
                </TableCell>
                <TableCell>
                  <Button onClick={() => navigate(event.id)}>Open</Button>
                  <Button
                    onClick={() => handleDeleteEvent(event)}
                    color="error"
                    variant="outlined"
                  >
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {!!error && <Typography color="error">{error.message}</Typography>}
      <Stack direction="row" spacing={1}>
        <Button onClick={() => refetch()} variant="contained">
          Refresh
        </Button>
        <Button
          color="success"
          onClick={() => navigate("new")}
          variant="contained"
        >
          Create New
        </Button>
      </Stack>
    </Box>
  );
}

const MY_EVENTS = gql`
  query {
    events {
      id
      name
      created
      roles
    }
  }
`;
